import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BookmarkX, Play, Clock, ArrowRight, Sparkles, Trash2, Share2, Download, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useLibrary } from '../lib/library';
import { decodePlaylist } from '../lib/playlistShare';
import { useProgress, formatSeconds } from '../lib/progress';
import EpisodePlayerModal from '../components/EpisodePlayerModal';
import BookmarkButton from '../components/BookmarkButton';
import PlaylistExportDialog from '../components/PlaylistExportDialog';

const REVEAL = { type: 'spring', stiffness: 520, damping: 26 };
const stripMd = (t) => (t || '').replace(/\*\*/g, '');

const LibraryPage = () => {
  const { ids, count, clear, toggle, has } = useLibrary();
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeEpisode, setActiveEpisode] = useState(null);
  const [resumeFrom, setResumeFrom] = useState(0);
  const [exportOpen, setExportOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const importToken = searchParams.get('p') || '';
  const [dismissedImport, setDismissedImport] = useState(false);

  useEffect(() => {
    api.get('/episodes?type=video')
      .then((r) => setEpisodes(r.data))
      .catch(() => setEpisodes([]))
      .finally(() => setLoading(false));
  }, []);

  // Preserve the user's save order (newest saved first).
  const saved = useMemo(() => {
    const byId = new Map(episodes.map((e) => [e.id, e]));
    return ids.map((id) => byId.get(id)).filter(Boolean);
  }, [ids, episodes]);

  // Incoming shareable-link import
  const importIds = useMemo(() => decodePlaylist(importToken), [importToken]);
  const importable = useMemo(() => {
    const byId = new Map(episodes.map((e) => [e.id, e]));
    return importIds
      .filter((id) => byId.get(id))
      .filter((id) => !has(id));
  }, [importIds, episodes, has]);
  const showImportBanner = !dismissedImport && importable.length > 0;

  const acceptImport = () => {
    importable.forEach((id) => toggle(id));
    toast.success(`Added ${importable.length} episode${importable.length === 1 ? '' : 's'} to your library`);
    setDismissedImport(true);
    setSearchParams({}, { replace: true });
  };

  const dismissImport = () => {
    setDismissedImport(true);
    setSearchParams({}, { replace: true });
  };

  const play = (ep, seekTo = 0) => {
    setResumeFrom(seekTo);
    setActiveEpisode(ep);
  };

  return (
    <div className="pt-28 pb-16 px-4 sm:px-6 min-h-screen">
      <div className="max-w-5xl mx-auto">
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={REVEAL}
          className="mb-10 sm:mb-12"
        >
          <span className="inline-block px-4 py-1.5 rounded-full glass text-[11px] tracking-[0.3em] uppercase text-gray-300 mb-4">
            Your Personal Playlist
          </span>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight" data-testid="library-heading">
                My <span className="gradient-text">Library</span>
              </h1>
              <p className="text-base sm:text-lg text-gray-400 mt-3 max-w-2xl">
                {count === 0
                  ? 'Bookmark episodes across the site and they’ll show up here — saved to this device.'
                  : `${count} episode${count === 1 ? '' : 's'} saved. Stored locally in your browser.`}
              </p>
            </div>
            {count > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setExportOpen(true)}
                  data-testid="library-export-open"
                  className="inline-flex items-center gap-2 px-4 py-2.5 btn-red rounded-full text-sm font-semibold"
                >
                  <Share2 className="w-4 h-4" /> Export
                </button>
                <button
                  type="button"
                  onClick={() => { if (window.confirm('Clear all bookmarks?')) clear(); }}
                  data-testid="library-clear-all"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full glass border border-white/10 hover:border-[#EF5B5B]/60 text-sm text-gray-300 hover:text-[#EF5B5B] smooth"
                >
                  <Trash2 className="w-4 h-4" /> Clear all
                </button>
              </div>
            )}
          </div>
        </motion.header>

        <AnimatePresence>
          {showImportBanner && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={REVEAL}
              className="mb-8 rounded-2xl glass-strong border border-[#EF5B5B]/40 p-4 sm:p-5 flex flex-wrap items-center gap-3"
              data-testid="library-import-banner"
              style={{ boxShadow: '0 20px 60px -30px rgba(239,91,91,0.5)' }}
            >
              <Download className="w-5 h-5 text-[#EF5B5B] shrink-0" />
              <div className="flex-1 min-w-[220px]">
                <p className="text-sm sm:text-base font-semibold">
                  Someone shared a playlist with you
                </p>
                <p className="text-xs sm:text-sm text-gray-400 mt-0.5">
                  {importable.length} new episode{importable.length === 1 ? '' : 's'} — add them to your library?
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={acceptImport}
                  data-testid="library-import-accept"
                  className="inline-flex items-center gap-2 px-4 py-2 btn-red rounded-full text-sm font-bold"
                >
                  Import all
                </button>
                <button
                  type="button"
                  onClick={dismissImport}
                  aria-label="Dismiss"
                  data-testid="library-import-dismiss"
                  className="w-9 h-9 rounded-full glass hover:bg-white/10 flex items-center justify-center smooth"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {loading ? (
          <p className="text-gray-500">Loading your library…</p>
        ) : count === 0 ? (
          <EmptyState />
        ) : saved.length === 0 ? (
          <p className="text-gray-500">Your saved episodes couldn’t be found. They may have been removed.</p>
        ) : (
          <div className="grid gap-5" data-testid="library-list">
            {saved.map((ep, i) => (
              <LibraryRow key={ep.id} ep={ep} index={i} onPlay={play} />
            ))}
          </div>
        )}
      </div>

      <EpisodePlayerModal
        episode={activeEpisode}
        open={!!activeEpisode}
        onClose={() => setActiveEpisode(null)}
        resumeFrom={resumeFrom}
      />

      <PlaylistExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        episodes={saved}
      />
    </div>
  );
};

const EmptyState = () => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={REVEAL}
    className="rounded-3xl glass-strong border border-[#EF5B5B]/20 p-10 sm:p-14 text-center"
    data-testid="library-empty"
    style={{ boxShadow: '0 30px 80px -40px rgba(239,91,91,0.35)' }}
  >
    <div className="inline-flex w-16 h-16 rounded-2xl bg-[#EF5B5B]/12 items-center justify-center mb-5">
      <Sparkles className="w-7 h-7 text-[#EF5B5B]" />
    </div>
    <h2 className="text-2xl sm:text-3xl font-bold mb-3">Your library is empty</h2>
    <p className="text-gray-400 max-w-md mx-auto mb-8">
      Tap the bookmark icon on any episode to save it here for later — no account needed.
    </p>
    <Link
      to="/episodes"
      data-testid="library-browse-episodes"
      className="inline-flex items-center gap-2 px-7 py-3.5 btn-red rounded-full font-bold"
    >
      Browse Episodes <ArrowRight className="w-4 h-4" />
    </Link>
  </motion.div>
);

const LibraryRow = ({ ep, onPlay }) => {
  const progress = useProgress(ep.id);
  const hasProgress = progress && progress.seconds >= 30;
  return (
  <motion.article
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={REVEAL}
    className="episode-card group relative flex flex-col sm:flex-row gap-5 sm:gap-6 p-4 sm:p-5 rounded-2xl glass-strong"
    data-testid="library-row"
  >
    <button
      type="button"
      onClick={() => onPlay(ep, hasProgress ? progress.seconds : 0)}
      className="relative shrink-0 w-full sm:w-48 aspect-square rounded-xl overflow-hidden group/thumb"
      aria-label={`Play ${ep.title}`}
    >
      <img src={ep.cover_art} alt={ep.title} className="w-full h-full object-cover group-hover/thumb:scale-105 smooth" loading="lazy" />
      <div className="absolute inset-0 bg-black/25 opacity-0 group-hover/thumb:opacity-100 smooth flex items-center justify-center">
        <div className="w-12 h-12 rounded-full bg-[#EF5B5B] flex items-center justify-center glow-red">
          <Play className="w-5 h-5 text-white ml-0.5" fill="currentColor" />
        </div>
      </div>
      {ep.duration && (
        <span className="absolute bottom-2.5 right-2.5 px-2.5 py-1 rounded-full bg-black/70 backdrop-blur-sm text-white text-[11px] font-semibold flex items-center gap-1 border border-white/10">
          <Clock className="w-3 h-3" /> {ep.duration}
        </span>
      )}
    </button>

    <div className="flex-1 min-w-0 flex flex-col">
      <div className="flex items-center gap-3 text-[11px] text-gray-500 uppercase tracking-widest mb-2">
        {ep.published_at && (
          <span>{new Date(ep.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        )}
      </div>
      <h3 className="text-xl sm:text-2xl font-bold leading-snug mb-3 group-hover:text-[#EF5B5B] smooth line-clamp-2">
        {ep.title}
      </h3>
      <p className="text-sm text-gray-400 leading-relaxed line-clamp-3 mb-4 flex-1">
        {stripMd(ep.description)}
      </p>
      <div className="flex items-center justify-between gap-3 mt-auto flex-wrap">
        <div className="inline-flex items-center gap-3">
          <button
            type="button"
            onClick={() => onPlay(ep, 0)}
            data-testid={`library-play-${ep.id}`}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#EF5B5B] hover:gap-3 smooth"
          >
            {hasProgress ? 'Restart' : 'Listen now'} <ArrowRight className="w-4 h-4" />
          </button>
          {hasProgress && (
            <button
              type="button"
              onClick={() => onPlay(ep, progress.seconds)}
              data-testid={`library-resume-${ep.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#EF5B5B]/12 border border-[#EF5B5B]/30 text-[11px] font-black uppercase tracking-widest text-[#EF5B5B] hover:bg-[#EF5B5B]/20 smooth"
            >
              Resume {formatSeconds(progress.seconds)}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {ep.reflection_slug && (
            <Link
              to={`/blog/${ep.reflection_slug}`}
              data-testid={`library-reflection-${ep.id}`}
              className="hidden sm:inline-flex px-3 py-1.5 rounded-full text-xs font-semibold text-gray-300 hover:text-[#EF5B5B] smooth"
            >
              Read reflection →
            </Link>
          )}
          <BookmarkButton ep={ep} size="sm" testId={`library-remove-${ep.id}`} />
        </div>
      </div>
    </div>

    <span className="absolute top-3 right-3 sm:hidden">
      <BookmarkX className="w-4 h-4 text-gray-500" />
    </span>
  </motion.article>
  );
};

export default LibraryPage;
